import { Icon, type IconKey } from "@core/assets";
import { cn } from "@core/utils";

type IllustrationKind = "empty" | "expired" | "mail" | "team";

interface IllustrationProps {
  kind: IllustrationKind;
  className?: string;
}

const BADGE: Record<IllustrationKind, { icon: IconKey; tone: string }> = {
  empty: { icon: "send", tone: "bg-menthe-600" },
  expired: { icon: "clock", tone: "bg-ink-700" },
  mail: { icon: "mail", tone: "bg-menthe-600" },
  team: { icon: "users", tone: "bg-menthe-600" },
};

/**
 * Placeholder for the framed illustrations (paper cards over a soft disc with a round icon) used
 * by empty states and the end-of-flow pages. Drawn here from inline shapes so the screens do not
 * wait; Raouf's exported assets replace the INSIDE of this component, nothing that uses it.
 */
export function Illustration({ kind, className }: IllustrationProps) {
  const { icon, tone } = BADGE[kind];

  return (
    <div aria-hidden className={cn("relative h-32 w-44 shrink-0", className)}>
      <svg viewBox="0 0 176 128" className="size-full" fill="none">
        <circle cx="88" cy="68" r="52" className="fill-ink-100" />
        <circle cx="26" cy="104" r="7" className="fill-ink-100" />
        <circle cx="152" cy="38" r="9" className="fill-ink-100" />
        <circle cx="30" cy="40" r="4" className="fill-ink-100" />
        <rect
          x="36"
          y="46"
          width="56"
          height="64"
          rx="6"
          transform="rotate(-12 64 78)"
          className="fill-ink-50 stroke-ink-200"
        />
        <rect
          x="84"
          y="46"
          width="56"
          height="64"
          rx="6"
          transform="rotate(12 112 78)"
          className="fill-ink-50 stroke-ink-200"
        />
        <rect
          x="60"
          y="26"
          width="56"
          height="76"
          rx="6"
          className="fill-white stroke-ink-200"
        />
        <rect
          x="70"
          y="38"
          width="26"
          height="4"
          rx="2"
          className="fill-ink-200"
        />
        <rect
          x="70"
          y="50"
          width="36"
          height="3"
          rx="1.5"
          className="fill-ink-100"
        />
        <rect
          x="70"
          y="58"
          width="30"
          height="3"
          rx="1.5"
          className="fill-ink-100"
        />
        <rect
          x="70"
          y="66"
          width="34"
          height="3"
          rx="1.5"
          className="fill-ink-100"
        />
      </svg>
      <span
        className={cn(
          "absolute top-[62%] left-1/2 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-white shadow-menu",
          tone
        )}
      >
        <Icon name={icon} size={20} />
      </span>
    </div>
  );
}

export type { IllustrationKind, IllustrationProps };
